import type { AssistantError, AssistantErrorCode } from '@agently/types';
import * as Sentry from '@sentry/react';

export type AssistantPhase = 'pre-stream' | 'stream' | 'sync';

// Expected outcomes: worth a trail in a later report, not a Sentry issue.
// Typed, so a renamed code is caught here. MODEL_ERROR, TIMEOUT and SEARCH_UNAVAILABLE
// are the model or search failing as they sometimes do: the spec's Sentry list does not name them.
const EXPECTED_CODES: ReadonlySet<string> = new Set<AssistantErrorCode>([
  'KNOWLEDGE_DISABLED',
  'MODEL_REFUSED',
  'RUN_IN_PROGRESS',
  'THREAD_NOT_FOUND',
  'UNAUTHORIZED',
  'SEARCH_UNAVAILABLE',
  'MODEL_ERROR',
  'TIMEOUT',
]);

const tagsOf = ({ code }: AssistantError, phase: AssistantPhase) => ({
  'assistant.error_code': code,
  'assistant.phase': phase,
});

/**
 * Unexpected Run failures go to Sentry; expected ones and drops while the page
 * is hidden (iOS suspending the PWA) leave a breadcrumb. Never question or Answer text.
 */
export function reportRunFailure(error: AssistantError, phase: AssistantPhase) {
  const hiddenDrop =
    error.code === 'NETWORK' && document.visibilityState === 'hidden';
  // A network failure outside the stream (before it opened, or while syncing)
  // is the member's connection.
  const offline = error.code === 'NETWORK' && phase !== 'stream';

  if (EXPECTED_CODES.has(error.code) || hiddenDrop || offline) {
    Sentry.addBreadcrumb({
      category: 'assistant',
      level: 'info',
      message: 'Run failed (expected)',
      data: { ...tagsOf(error, phase), status: error.status },
    });
    return;
  }

  Sentry.captureException(new Error(`Assistant run failed: ${error.code}`), {
    tags: tagsOf(error, phase),
    extra: { status: error.status },
  });
}

export function reportStop() {
  Sentry.addBreadcrumb({
    category: 'assistant',
    level: 'info',
    message: 'Run stopped',
  });
}
